export function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

// Capitalises the first letter only. The model writes drivers and some picks
// in lower case (they were chips once), and a deck or a subhead opens with a
// capital; the rest of the string, "AM", place names and all, is left alone.
export function sentenceCase(s) {
  return typeof s === 'string' && s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export function formatShortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

// "Sat, Sep 26, 2026": the phone dateline, where the long form wrapped to
// two rows under the masthead.
export function formatDatelineShort(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

// `zone: false` drops the "EDT" for the phone dateline. The time is still
// Eastern either way.
export function formatUpdatedAt(iso, { zone = true } = {}) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    ...(zone ? { timeZoneName: 'short' } : {}),
    timeZone: 'America/New_York'
  });
}

export function formatTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, '0')} ${period}`;
}

// A dash between two clock times is always a range, so it reads as one. The
// bench and the events feed write "8 AM to 2 PM" since 2026-09-20, but a brief
// is a stored blob: anything synthesized before that keeps its dashes forever,
// because archived briefs never regenerate. These are the render-time guard.
//
// Two shapes, on purpose. A field that holds nothing but a time can take a
// blanket replace. A headline is prose ("Second Sunday on King at King St,
// Queen to Calhoun, 12 to 5 PM") where a dash might belong to an event name,
// so there the replacement only fires between two clock readings.
const CLOCK = String.raw`\d{1,2}(?::\d{2})?\s*(?:[AP]\.?M\.?)?`;
const TIME_RANGE = new RegExp(String.raw`(${CLOCK})\s*[–—]\s*(${CLOCK})`, 'gi');

export function timeLabel(time) {
  return (time || '').replace(/\s*[–—]\s*/g, ' to ');
}

export function timeRangesToWords(text) {
  return (text || '').replace(TIME_RANGE, '$1 to $2');
}

// 26, not 24: the brief lands in the 6 AM hour and a run a few minutes late
// must not trip the banner on an otherwise healthy day. Exported so the banner
// can state the threshold it is actually using. The banner used to say "over
// 24 hours" against this 26, which made it wrong twice: silent for two hours
// when the brief really had gone stale, then misdescribing itself when it
// finally fired.
export const STALE_HOURS = 26;

export function isStale(generatedAt, hours = STALE_HOURS) {
  if (!generatedAt) return true;
  const generated = new Date(generatedAt).getTime();
  const now = Date.now();
  return now - generated > hours * 60 * 60 * 1000;
}

// Current calendar date in Eastern time as YYYY-MM-DD (en-CA gives ISO order).
// Brief dates are ET days, so "is this brief today's?" must compare in ET, not
// the viewer's zone.
export function etToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
}

// Minutes past midnight in Charleston, for placing "now" on today's tide.
// hourCycle h23, because hour12: false prints midnight as "24" in some engines.
export function etMinutesNow(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23'
  }).formatToParts(date);
  const part = (type) => Number(parts.find((p) => p.type === type)?.value || 0);
  return part('hour') * 60 + part('minute');
}

export function etDateOf(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date(iso));
}

export function weekdayName(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long' });
}

// SERP description built from structured brief fields as whole sentences,
// capped without ever cutting mid-word. Replaces slicing raw brief text.
export function composeBriefDescription(brief, max = 155) {
  const fallback = 'Daily conditions, picks, and tides for visitors to Charleston, SC.';
  const headline = brief?.score?.headline;
  if (!headline) return fallback;

  const picks = brief.score?.topPicks || {};
  const candidates = [
    picks.morning && `Morning: ${picks.morning}.`,
    picks.afternoon && `Afternoon: ${picks.afternoon}.`,
    picks.evening && `Evening: ${picks.evening}.`
  ].filter(Boolean);
  const closer = 'A new brief every morning by 6:30 ET.';

  let out = `Charleston today: ${headline}.`;
  for (const sentence of candidates) {
    if (`${out} ${sentence} ${closer}`.length > max) break;
    out = `${out} ${sentence}`;
  }
  if (`${out} ${closer}`.length <= max) out = `${out} ${closer}`;
  return out.length <= max ? out : fallback;
}
